/**
 * Environment-First Bridge (2025 Standard - Updated Jan 2, 2026)
 *
 * Orchestrates communication between Dope Chain Wars and host environments:
 * 1. Farcaster Mini Apps (Frames v2 - @farcaster/miniapp-sdk)
 * 2. Base Mini Apps (Base MiniKit via OnchainKit)
 * 3. World App (@worldcoin/minikit-js v2)
 * 4. Web / PWA (RainbowKit + wagmi)
 *
 * CRITICAL FIX (Jan 2, 2026):
 * - DYNAMIC SDK import to prevent errors on non-Farcaster environments
 * - sdk.actions.ready() called IMMEDIATELY after SDK loads (before await context)
 * - Consolidated with authBridge.js to avoid duplicate imports
 */

import { logger } from '../utils/logger.js';
import useGameState from './gameState';
import getBaseMiniKitClient from './wallet/baseMiniKit';
import { trackWalletSuccess } from '../utils/analytics.js';

// ---------------------------------------------------------------------------
// DYNAMIC SDK LOADING - Prevents errors on desktop/non-Farcaster
// ---------------------------------------------------------------------------
let sdk = null;
let sdkLoadPromise = null;

async function loadFarcasterSdk() {
  if (sdk) return sdk;
  if (sdkLoadPromise) return sdkLoadPromise;
  
  sdkLoadPromise = (async () => {
    try {
      const module = await import('@farcaster/miniapp-sdk');
      // Fix: Better SDK extraction for different export patterns
      sdk = module.sdk || module.default || module;
      console.log('[frameBridge] Farcaster SDK loaded dynamically');
      
      // Fix: Ensure ready() is called correctly per 2025 standard
      if (sdk?.actions?.ready && typeof window !== 'undefined') {
        // Only call ready() in actual Farcaster environment
        const isFarcasterEnv = /farcaster|warpcast/i.test(navigator.userAgent) || 
                              window.location.href.includes('farcaster') ||
                              document.referrer.includes('warpcast');
        
        if (isFarcasterEnv) {
          await sdk.actions.ready(); // Fix: await the ready call for proper sequencing
          console.log('[frameBridge] 🚀 sdk.actions.ready() completed successfully');
          window.DCW_DIAGNOSTICS && (window.DCW_DIAGNOSTICS.sdkReadyCalled = true);
        }
      }
      
      return sdk;
    } catch (e) {
      console.warn('[frameBridge] Farcaster SDK load failed (expected on desktop):', e.message);
      return null;
    }
  })();
  
  return sdkLoadPromise;
}

// ---------------------------------------------------------------------------
// EARLY DETECTION & READY CALL
// ---------------------------------------------------------------------------
// For Farcaster, we MUST call ready() ASAP. This runs on module load.
if (typeof window !== 'undefined') {
  const ua = navigator.userAgent || '';
  const href = window.location.href || '';
  const referrer = document.referrer || '';
  
  // Check if we're likely in Farcaster environment
  const isFarcasterLikely = (
    /warpcast|farcaster/i.test(ua) ||
    /warpcast|farcaster/i.test(referrer) ||
    /warpcast|farcaster|\.fc\./i.test(href) ||
    window.farcaster ||
    window.__farcaster_miniapp__ ||
    (window.miniKit && window.miniKit.isFrameContext === true)
  );
  
  if (isFarcasterLikely) {
    console.log('[frameBridge] 🟣 Farcaster environment detected early - loading SDK immediately');
    // Load SDK and call ready() ASAP (non-blocking)
    loadFarcasterSdk().catch(e => console.warn('[frameBridge] Early SDK load failed:', e));
  }
}

// ---------------------------------------------------------------------------
// State & Storage
// ---------------------------------------------------------------------------
// let sdk = null; // Removed local sdk var to use imported one
let initialized = false;
let environment = 'web'; // 'farcaster' | 'base' | 'world' | 'web'
let contextUser = null;  // User data from native environment

const listeners = {
  delta: new Set(),
  error: new Set(),
  context: new Set(),
};

// ---------------------------------------------------------------------------
// Initialization (The "Black Screen" Fix)
// ---------------------------------------------------------------------------
export async function initFrameBridge() {
  if (initialized) return;

  logger.info('[frameBridge] 🚀 Initializing Environment-First Bridge...');

  // 1. Detect Environment
  await detectEnvironment();

  // 2. For Farcaster, ensure SDK is loaded and ready() is called
  if (environment === 'farcaster') {
    const farcasterSdk = await loadFarcasterSdk();
    if (farcasterSdk?.actions?.ready) {
      // Safety call - might be redundant but harmless
      try {
        farcasterSdk.actions.ready();
        logger.info('[frameBridge] (Safety) sdk.actions.ready() called in init');
      } catch (e) { /* ignore */ }
    }
  }

  // 3. Initialize specific environment logic
  if (environment === 'farcaster') {
    await initFarcaster();
  } else if (environment === 'base') {
    await initBase();
  } else if (environment === 'world') {
    await initWorld();
  }

  initialized = true;
  logger.info(`[frameBridge] ✅ Initialization complete. Environment: ${environment}`);
}

// ---------------------------------------------------------------------------
// Environment Detection & SDK Loading
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Environment Detection & SDK Loading
// ---------------------------------------------------------------------------
import { detectPlatform, PLATFORM } from './device.js';

async function detectEnvironment() {
  const platform = detectPlatform();

  switch (platform) {
    case PLATFORM.FARCASTER:
      environment = 'farcaster';
      logger.info('[frameBridge] 🟣 Farcaster Environment Detected (via device.js)');
      break;
    case PLATFORM.BASE_MINIKIT:
      environment = 'base';
      logger.info('[frameBridge] 🔵 Base App Environment Detected (via device.js)');
      break;
    case PLATFORM.WORLDCOIN:
      environment = 'world';
      logger.info('[frameBridge] 🌍 World App Environment Detected (via device.js)');
      break;
    default:
      environment = 'web';
      logger.info(`[frameBridge] 🌐 Standard Web/PWA Environment (Default) - Platform: ${platform}`);
      break;
  }
}

// Removed legacy waitForFarcasterSDK logic


// ---------------------------------------------------------------------------
// Farcaster Specific Logic (Context Auth)
// ---------------------------------------------------------------------------
async function initFarcaster() {
  try {
    const farcasterSdk = await loadFarcasterSdk();
    if (!farcasterSdk) {
      logger.warn('[frameBridge] Farcaster SDK not available for context fetch');
      return;
    }
    
    // Fix: Context is synchronous in 2025 SDK, not a Promise
    const context = farcasterSdk.context;
    if (context?.user) {
      logger.info('[frameBridge] 👤 Farcaster Context Received:', context.user.username);
      contextUser = {
        type: 'farcaster',
        fid: context.user.fid,
        username: context.user.username,
        display_name: context.user.displayName,
        pfp_url: context.user.pfpUrl,
        custody_address: context.user.custodyAddress,
        // Map verifications if needed
      };
      emit('context', contextUser);
    }
  } catch (e) {
    logger.error('[frameBridge] Failed to get Farcaster context:', e);
  }
}

// ---------------------------------------------------------------------------
// Base Specific Logic
// ---------------------------------------------------------------------------
async function initBase() {
  const client = getBaseMiniKitClient();
  const success = await client.initialize();

  if (success) {
    logger.info('[frameBridge] 🔵 Base MiniKit Initialized');

    // Check if already connected/has context
    if (client.context?.user) {
      contextUser = {
        type: 'base',
        address: await client.getAddress(), // Might need connect()
        username: client.context.user.username,
      };
      emit('context', contextUser);
    }
  } else {
    logger.warn('[frameBridge] ⚠️ Base MiniKit Init Failed');
  }
}

// ---------------------------------------------------------------------------
// World Specific Logic
// ---------------------------------------------------------------------------
async function initWorld() {
  try {
    // Lazy-load World App MiniKit v2
    const { MiniKit } = await import('@worldcoin/minikit-js');
    
    // CRITICAL FIX: MiniKit.install() requires appId parameter
    let appId = import.meta.env.VITE_MINIKIT_PROJECT_ID || import.meta.env.VITE_WC_PROJECT_ID;
    if (!appId || appId === '***************') {
      logger.error('[frameBridge] VITE_MINIKIT_PROJECT_ID not configured, using fallback');
      appId = 'app_staging_92c30089e8751fa049b1093121516e45'; // Generic fallback
    }
    
    appId = String(appId);
    
    logger.info('[frameBridge] 🌍 Installing World App MiniKit with appId:', appId?.substring(0, 10) + '...');
    
    // Install MiniKit (v2 API) with appId
    try {
      await MiniKit.install(appId);
    } catch (installError) {
      try {
        MiniKit.install(appId);
      } catch (syncError) {
        logger.error('[frameBridge] Sync MiniKit.install also failed:', syncError);
      }
    }
    
    logger.info('[frameBridge] 🌍 World App MiniKit Installed');
    
    // Check if already authenticated
    if (MiniKit.isInstalled() && MiniKit.user) {
      contextUser = {
        type: 'world',
        address: MiniKit.user.walletAddress,
        username: MiniKit.user.username || 'World User',
        worldId: MiniKit.user.worldId,
      };
      emit('context', contextUser);
    }
  } catch (e) {
    logger.error('[frameBridge] World Init Error', e);
  }
}


// ---------------------------------------------------------------------------
// Unified Auth Method (The "Login" Router)
// ---------------------------------------------------------------------------
/**
 * Main login entry point.
 * Returns: { user, type } or null (if UI should use fallback)
 */
export async function login() {
  if (!initialized) await initFrameBridge();

  logger.info(`[frameBridge] Login requested for environment: ${environment}`);

  switch (environment) {
    case 'farcaster':
      // Farcaster is "silent auth" - we should already have the user from context
      if (contextUser) {
        trackWalletSuccess('farcaster', 'context_auth', true);
        return { ...contextUser, success: true };
      } else {
        // If context failed, try re-fetching
        await initFarcaster();
        if (contextUser) {
          trackWalletSuccess('farcaster', 'context_auth_retry', true);
          return { ...contextUser, success: true };
        } else {
          trackWalletSuccess('farcaster', 'context_auth', false);
          return null;
        }
      }

    case 'base':
      logger.info('[frameBridge] Triggering Base Native Login...');
      try {
        const client = getBaseMiniKitClient();
        const address = await client.connect(); // Triggers eth_requestAccounts
        if (address) {
          trackWalletSuccess('base', 'connect', true);
          return {
            type: 'base',
            address: address,
            username: client.context?.user?.username || 'Base User',
            success: true
          };
        }
      } catch (e) {
        trackWalletSuccess('base', 'connect', false, { error: e.message });
        logger.error('[frameBridge] Base Login Failed', e);
      }
      return null;

    case 'world':
      logger.info('[frameBridge] Triggering World App Native Login...');
      try {
        // Use World App MiniKit v2 API
        const { MiniKit } = await import('@worldcoin/minikit-js');
        
        if (!MiniKit.isInstalled()) {
          logger.error('[frameBridge] MiniKit not installed');
          return null;
        }
        
        // Trigger wallet auth (MiniKit v2 API)
        const payloadResponse = await MiniKit.commandsAsync.walletAuth({
          nonce: crypto.randomUUID().replace(/-/g, ''), // Generate random nonce
          expirationTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
          notBefore: new Date(),
          statement: 'Sign in to Dope Chain Wars',
        });
        
        const finalPayload = payloadResponse.finalPayload || payloadResponse;
        
        if (finalPayload && finalPayload.status !== 'error') {
          trackWalletSuccess('world', 'wallet_auth', true);
          return {
            type: 'world',
            address: finalPayload.address,
            signature: finalPayload.signature,
            message: finalPayload.message || 'context-auth',
            worldId: MiniKit.user?.worldId,
            success: true
          };
        } else {
          trackWalletSuccess('world', 'wallet_auth', false, { error: finalPayload?.status });
        }
      } catch (e) {
        trackWalletSuccess('world', 'wallet_auth', false, { error: e.message });
        logger.error('[frameBridge] World Login Failed', e);
      }
      return null;

    case 'web':
    default:
      // Return null tells AuthContext to use RainbowKit
      return null;
  }
}


// ---------------------------------------------------------------------------
// Event Bus & Helpers
// ---------------------------------------------------------------------------
export function getEnvironment() {
  return environment;
}

export function onContext(cb) {
  listeners.context.add(cb);
  return () => listeners.context.delete(cb);
}

function emit(event, payload) {
  listeners[event]?.forEach((cb) => {
    try { cb(payload); } catch (e) { console.error(e); }
  });
}

// ---------------------------------------------------------------------------
// TURN QUEUEING SYSTEM (2025 SDK Implementation)
// ---------------------------------------------------------------------------

const turnQueue = [];
let flushTimer = null;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_QUEUE_SIZE = 10;

function generateActionId() {
  return `act_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function getSessionToken() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('dcw_api_key') || localStorage.getItem('dcw_wallet');
  }
  return null;
}

function getAuthToken() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('dcw_access_token') || localStorage.getItem('dcw_api_key');
  }
  return null;
}

function applyStateUpdate(stateUpdate) {
  /**
   * Apply server-side state updates to local game state.
   * 
   * @param {Object} stateUpdate - State changes from server
   */
  try {
    const gameState = useGameState.getState();
    if (!gameState || !gameState.setState) {
      logger.warn('[frameBridge] Cannot apply state update: game state unavailable');
      return;
    }

    gameState.setState((prev) => ({
      ...prev,
      ...stateUpdate,
      lastSync: Date.now()
    }));

    logger.debug('[frameBridge] State update applied', stateUpdate);
  } catch (error) {
    logger.error('[frameBridge] Failed to apply state update', error);
  }
}

/**
 * Queue a player action for batch processing.
 * 
 * Batches player actions into turns and flushes them to the backend
 * at appropriate intervals. This enables async gameplay where actions
 * are processed in rounds.
 * 
 * @param {Object} action - The player action
 * @param {string} action.type - Action type (buy, sell, move, chat, use_item)
 * @param {Object} action.payload - Action-specific data
 * @param {string} [action.payload.borough] - Target borough for move/sell
 * @param {string} [action.payload.drug_type] - Drug type for buy/sell
 * @param {number} [action.payload.quantity] - Quantity for buy/sell
 * @param {string} [action.payload.message] - Message for chat
 */
export function queueAction(action) {
  if (!action || !action.type) {
    logger.error('[frameBridge] Invalid action queued', action);
    return;
  }

  const queuedAction = {
    ...action,
    id: generateActionId(),
    queuedAt: Date.now(),
    clientTimestamp: Date.now()
  };

  turnQueue.push(queuedAction);

  logger.debug('[frameBridge] Action queued', { 
    type: action.type, 
    queueLength: turnQueue.length,
    actionId: queuedAction.id
  });

  // Auto-flush if queue reaches threshold
  if (turnQueue.length >= MAX_QUEUE_SIZE) {
    flushTurn();
  }

  // Ensure flush timer is running
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushTurn();
    }, FLUSH_INTERVAL);
  }
}

/**
 * Flush queued actions to the backend.
 * 
 * Sends all queued actions to the Virtuals turn endpoint
 * and processes the response (updated game state).
 * 
 * @returns {Promise<Object>} Backend response with state updates
 */
export async function flushTurn() {
  // Clear any pending timer
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (turnQueue.length === 0) {
    return Promise.resolve({ success: true, empty: true });
  }

  const actions = [...turnQueue];
  turnQueue.length = 0; // Clear queue

  const sessionToken = getSessionToken();
  const authToken = getAuthToken();

  if (!sessionToken) {
    logger.warn('[frameBridge] Cannot flush turn: no session token');
    // Re-queue actions for retry when session available
    turnQueue.unshift(...actions);
    return Promise.resolve({ success: false, error: 'No session' });
  }

  try {
    const apiUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL
      ? `${import.meta.env.VITE_BACKEND_URL}/api/v1/virtuals/turn`
      : '/api/v1/virtuals/turn';

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : ''
      },
      body: JSON.stringify({
        session_token: sessionToken,
        player_actions: actions,
        client_timestamp: Date.now(),
        platform: environment
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Turn flush failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();

    // Update local game state with server response
    if (result.state_update) {
      applyStateUpdate(result.state_update);
    }

    // Log any encounters
    if (result.encounter) {
      logger.info('[frameBridge] Encounter triggered', result.encounter);
      emit('encounter', result.encounter);
    }

    logger.info('[frameBridge] Turn flushed successfully', {
      actionsSent: actions.length,
      turnsRemaining: result.turns_remaining,
      encounter: !!result.encounter
    });

    return result;
  } catch (error) {
    // Re-queue actions on failure for retry
    turnQueue.unshift(...actions);
    logger.error('[frameBridge] Turn flush failed, requeued for retry', error);
    
    // Emit error event for UI handling
    emit('error', { type: 'turn_flush_failed', error: error.message });
    
    throw error;
  }
}

/**
 * Get current turn queue status.
 * 
 * @returns {Object} Queue status information
 */
export function getTurnQueueStatus() {
  return {
    queuedActions: turnQueue.length,
    maxSize: MAX_QUEUE_SIZE,
    flushInterval: FLUSH_INTERVAL,
    hasPendingFlush: !!flushTimer
  };
}

/**
 * Clear the turn queue without flushing.
 * Use with caution - primarily for logout/reset scenarios.
 */
export function clearTurnQueue() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  turnQueue.length = 0;
  logger.info('[frameBridge] Turn queue cleared');
}



// ---------------------------------------------------------------------------
// Unified Context Access (Legacy Support -> New Standard)
// ---------------------------------------------------------------------------

export async function getContext() {
  if (!initialized) await initFrameBridge();
  return contextUser || null;
}

export async function getCurrentUser() {
  if (!initialized) await initFrameBridge();

  if (contextUser) {
    // Map to expected format if needed, or return direct
    return {
      address: contextUser.custody_address || contextUser.address, // Handle both Farcaster & Base
      fid: contextUser.fid,
      username: contextUser.username,
      displayName: contextUser.display_name || contextUser.username,
      platform: contextUser.type,
      profileImage: contextUser.pfp_url
    };
  }
  return null;
}

export function isConnected() {
  return !!contextUser; // Simple check for active session
}

export function getCurrentPlatform() {
  return environment;
}

export async function signOut() {
  localStorage.removeItem('dcw_api_key');
  localStorage.removeItem('dcw_wallet_address');
  localStorage.removeItem('dcw_platform_context');
  logger.info('[frameBridge] User signed out');
}

// ---------------------------------------------------------------------------
// Helper to share content via Farcaster
// ---------------------------------------------------------------------------
export async function shareToFarcaster(options) {
  if (!initialized) await initFrameBridge();

  const farcasterSdk = await loadFarcasterSdk();
  if (farcasterSdk?.actions?.share) {
    return farcasterSdk.actions.share(options);
  }

  return false;
}

// ---------------------------------------------------------------------------
// Auto-flush disabled - was calling flushTurn() 60x/sec generating console spam
// The flushTurn stub doesn't do anything useful until turn queueing is implemented
// ---------------------------------------------------------------------------
// NOTE: When turn queueing is implemented, re-enable with a throttled approach:
// if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.VITEST)) {
//   let lastFlush = 0;
//   const tick = () => {
//     const now = performance.now();
//     if (now - lastFlush > 1000) { // Flush at most once per second
//       flushTurn();
//       lastFlush = now;
//     }
//     requestAnimationFrame(tick);
//   };
//   requestAnimationFrame(tick);
// }